import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { theme } from '@assembly/theme'
import { ThemeProvider } from '@mui/material/styles'
import { SideNavbar, ProtectedRoute } from '@assembly/components'
import { RoutePaths } from '@assembly/constants'
import {
  AssemblyLine,
  AssemblyLines,
  AssemblyTemplate,
  AssemblyTemplates,
  Login,
  MainSSOCallback,
  Organization,
  OrganizationManager,
  Resource,
  Resources,
  Team,
  TeamMember,
  Teams,
  Debug,
  Skills,
  SkillsAssessment,
  ResourcePayoutEntries,
  ResourcePayoutInvoices,
  Billing,
  Invoices,
  TalentInvoices,
  ClientInvoices,
  UsageBilling,
  Contracts,
  Candidates,
  Jobs,
} from '@assembly/pages'
import { AppProvider, SnackBarProvider } from '@assembly/contexts'
import Capabilities from './pages/Capabilities'
import ClientInvoiceDetail from './pages/ClientInvoices/ClientInvoiceDetail'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import React from 'react'
import { checkSessionIsValid } from './api/auth'
import { User } from '@assembly/shared/types/user'
import { Roles } from './pages/Teams/types'
import moment from 'moment-timezone'

moment.tz.setDefault('America/Los_Angeles')

function App() {
  const didMountRef = React.useRef(false)
  const [showLogin, setShowLogin] = React.useState<boolean>(true)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [userInfo, setUserInfo] = React.useState<User | null>(null)

  const checkSession = React.useCallback(() => {
    checkSessionIsValid()
      .then((response) => {
        if (response?.data && typeof response.data === 'object' && (response.data as any).id) {
          setUserInfo(response.data)
          setShowLogin(false)
        } else {
          setShowLogin(true)
          setUserInfo(null)
        }
      })
      .catch(() => {
        setShowLogin(true)
        setUserInfo(null)
      })
  }, [])

  React.useEffect(() => {
    if (didMountRef.current) {
      return
    }

    didMountRef.current = true
    checkSessionIsValid()
      .then((response) => {
        setUserInfo(response.data)
        setShowLogin(false)
      })
      .catch(() => {
        setShowLogin(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const wrapInSideNavBar = (Component: React.ComponentType) => {
    return (
      <SideNavbar>
        <ProtectedRoute>
          <Component />
        </ProtectedRoute>
      </SideNavbar>
    )
  }

  // Function to get the default home route based on user role
  const getDefaultHomeRoute = (userRole: Roles): string => {
    // For RECRUITER, default to /resources as it's the first accessible page
    if (userRole === Roles.Recruiter) {
      return RoutePaths.Resources
    }

    // For INTERNAL_ADMIN and SUPER_ADMIN, default to /orgs
    if (userRole === Roles.InternalAdmin || userRole === Roles.SuperAdmin) {
      return RoutePaths.OrganizationManager
    }

    // For ORCHESTRATION_ADMIN, redirect to login since they have no access
    if (userRole === Roles.OrchestrationAdmin) {
      return RoutePaths.Login
    }

    // Default fallback (shouldn't happen with current roles)
    return RoutePaths.Resources
  }

  const renderContent = () => {
    if (showLogin) {
      const pathname = window.location.pathname.replace(/\/$/, '')
      if (pathname === RoutePaths.MainSSOCallback) {
        return <MainSSOCallback />
      }
      return <Login onLoginSuccess={checkSession} />
    }

    if (isLoading) {
      return <div></div>
    }

    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={<Navigate to={getDefaultHomeRoute(userInfo?.role as Roles)} replace />}
          />
          <Route
            path={RoutePaths.OrganizationManager}
            element={wrapInSideNavBar(OrganizationManager)}
          />
          <Route
            path={RoutePaths.Organization}
            element={wrapInSideNavBar(Organization)}
          />
          <Route path={RoutePaths.Teams} element={wrapInSideNavBar(Teams)} />
          <Route path={RoutePaths.Team} element={wrapInSideNavBar(Team)} />
          <Route
            path={RoutePaths.TeamMember}
            element={wrapInSideNavBar(TeamMember)}
          />
          <Route
            path={RoutePaths.Resources}
            element={wrapInSideNavBar(Resources)}
          />
          <Route
            path={RoutePaths.Resource}
            element={wrapInSideNavBar(Resource)}
          />
          <Route
            path={RoutePaths.ResourcePayoutEntries}
            element={wrapInSideNavBar(ResourcePayoutEntries)}
          />
          <Route
            path={RoutePaths.ResourcePayoutInvoices}
            element={wrapInSideNavBar(ResourcePayoutInvoices)}
          />
          <Route
            path={RoutePaths.Billing}
            element={wrapInSideNavBar(Billing)}
          />
          <Route
            path={RoutePaths.UsageBilling}
            element={wrapInSideNavBar(UsageBilling)}
          />
          <Route
            path={RoutePaths.UsageBillingWithAssembly}
            element={wrapInSideNavBar(UsageBilling)}
          />
          <Route
            path={RoutePaths.Contracts}
            element={wrapInSideNavBar(Contracts)}
          />
          <Route
            path={RoutePaths.Invoices}
            element={wrapInSideNavBar(Invoices)}
          />
          <Route
            path={RoutePaths.TalentInvoices}
            element={wrapInSideNavBar(TalentInvoices)}
          />
          <Route
            path={RoutePaths.ClientInvoices}
            element={wrapInSideNavBar(ClientInvoices)}
          />
          <Route
            path={RoutePaths.ClientInvoice}
            element={wrapInSideNavBar(ClientInvoiceDetail)}
          />
          <Route
            path={RoutePaths.Capabilities}
            element={wrapInSideNavBar(Capabilities)}
          />
          <Route path={RoutePaths.Skills} element={wrapInSideNavBar(Skills)} />
          <Route
            path={RoutePaths.AssemblyTemplates}
            element={wrapInSideNavBar(AssemblyTemplates)}
          />
          <Route
            path={RoutePaths.AssemblyTemplate}
            element={wrapInSideNavBar(AssemblyTemplate)}
          />
          <Route
            path={RoutePaths.AssemblyLines}
            element={wrapInSideNavBar(AssemblyLines)}
          />
          <Route
            path={RoutePaths.AssemblyLine}
            element={wrapInSideNavBar(AssemblyLine)}
          />
          <Route
            path={RoutePaths.SkillsAssessment}
            element={wrapInSideNavBar(SkillsAssessment)}
          />
          <Route
            path={RoutePaths.Candidates}
            element={wrapInSideNavBar(Candidates)}
          />
          <Route path={RoutePaths.Jobs} element={wrapInSideNavBar(Jobs)} />
          {(userInfo?.role === Roles.InternalAdmin || userInfo?.role === Roles.SuperAdmin) && (
            <Route path={RoutePaths.Debug} element={wrapInSideNavBar(Debug)} />
          )}
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
      <AppProvider user={userInfo as User}>
        <ThemeProvider theme={theme}>
          <SnackBarProvider>{renderContent()}</SnackBarProvider>
        </ThemeProvider>
      </AppProvider>
    </LocalizationProvider>
  )
}

export default App
